// ------------------------------------------------------------------
// API DTOs (aligned with api.json contract — tag: Tasks)
// ------------------------------------------------------------------

/**
 * Minimal task shape returned by `GET /api/projects/{projectId}/gantt`.
 * Intentionally NOT used to drive rows: it lacks status, priority, parentId
 * and group, so it cannot color bars, group, or build hierarchy. Rows come
 * from the rich task list (`view=gantt`); from this endpoint we use only
 * `dependencies`.
 */
export interface GanttTaskDto {
  id: string;
  title: string;
  startDate: string | null;
  dueDate: string | null;
  estimatedHours: number | null;
  assigneeId: string | null;
  isCompleted: boolean;
}

/**
 * Dependency edge. `type` is a free-form string in the spec; we normalize it
 * to {@link GanttLinkType} in the adapter. Backend creates FS only.
 */
export interface GanttDependencyDto {
  id: string;
  predecessorTaskId: string;
  successorTaskId: string;
  type: string | null;
  lagDays: number;
}

export interface GanttDto {
  tasks: GanttTaskDto[];
  dependencies: GanttDependencyDto[];
}

/** Body for `POST .../dependencies`. Creation is finish-to-start only. */
export interface AddTaskDependencyRequest {
  predecessorTaskId: string;
  lagDays: number;
}

// ------------------------------------------------------------------
// Normalized model (agnostic — consumed by GanttChart, never the raw DTO)
// ------------------------------------------------------------------

/**
 * Dependency link types. The backend only creates FS, but the chart renders
 * any of the four if the backend returns them.
 */
export const GANTT_LINK_TYPE = {
  FinishToStart: 'FS',
  StartToStart: 'SS',
  FinishToFinish: 'FF',
  StartToFinish: 'SF',
} as const;

export type GanttLinkType = (typeof GANTT_LINK_TYPE)[keyof typeof GANTT_LINK_TYPE];

/** Assignee data the chart needs for the left-grid avatar. Decoupled from the task domain. */
export interface GanttRowAssignee {
  id: string;
  name: string;
  avatarUrl?: string;
  /** Optional fallback color; the avatar component derives one when absent. */
  color?: string;
}

/**
 * A single timeline row. Synthetic group headers (when `groupBy ≠ none`) are
 * regular rows with `isGroupHeader: true` and no `taskId`, parenting their
 * member rows via `parentId` — this is how grouping is achieved without the
 * PRO grouping feature.
 */
export interface GanttRow {
  id: string;
  title: string;
  startDate: Date | null;
  endDate: Date | null;
  /** 0–100. Header rows aggregate their children; date-less tasks are 0. */
  progress: number;
  parentId: string | null;
  isGroupHeader: boolean;
  isCompleted: boolean;
  /** Bar color resolved from status or priority. Absent for header rows. */
  color?: string;
  assignee?: GanttRowAssignee | null;
  /** Real task id; absent for synthetic group headers. */
  taskId?: string;
}

export interface GanttLink {
  id: string;
  /** predecessorTaskId */
  from: string;
  /** successorTaskId */
  to: string;
  type: GanttLinkType;
  lagDays: number;
}

export interface GanttModel {
  rows: GanttRow[];
  links: GanttLink[];
}

// ------------------------------------------------------------------
// View + interaction state
// ------------------------------------------------------------------

export type GanttZoom = 'day' | 'week' | 'month';

export type GanttGroupBy = 'none' | 'status' | 'priority' | 'assignee' | 'group';

export interface ScheduleRange {
  startDate: Date;
  endDate: Date;
}

/** A pending drag/resize: original vs candidate dates for a single task. */
export interface ScheduleChange {
  taskId: string;
  title: string;
  from: ScheduleRange;
  to: ScheduleRange;
}

/** A dependency the pending {@link ScheduleChange} would violate. */
export interface Conflict {
  linkId: string;
  predecessorTaskId: string;
  successorTaskId: string;
  predecessorTitle: string;
  successorTitle: string;
}
